/**
 * SCP Social Sharing Component
 * Handles copy to clipboard functionality for social sharing
 */

if (!customElements.get("scp-social-sharing")) {
  customElements.define(
    "scp-social-sharing",
    class SCPSocialSharing extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.copyButtons = this.querySelectorAll("[data-copy-link]");
        this.message = this.querySelector("[data-copy-message]");
        this.init();
      }

      init() {
        this.copyButtons.forEach((button) => {
          button.addEventListener("click", (e) => this.handleCopyClick(e));
        });
      }

      async handleCopyClick(event) {
        const button = event.currentTarget;
        const url = button.getAttribute("data-copy-link");

        try {
          // Try using the modern Clipboard API
          if (navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(url);
            this.showSuccess(button);
          } else {
            // Fallback for older browsers or non-secure contexts
            this.fallbackCopyToClipboard(url, button);
          }
        } catch (err) {
          console.error("Failed to copy link:", err);
          this.fallbackCopyToClipboard(url, button);
        }
      }

      fallbackCopyToClipboard(text, button) {
        // Create a temporary textarea element
        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.style.position = "fixed";
        textarea.style.left = "-999999px";
        textarea.style.top = "-999999px";
        document.body.appendChild(textarea);

        // Select and copy the text
        textarea.focus();
        textarea.select();

        try {
          const successful = document.execCommand("copy");
          if (successful) {
            this.showSuccess(button);
          } else {
            console.error("Fallback: Copy command was unsuccessful");
          }
        } catch (err) {
          console.error("Fallback: Unable to copy", err);
        }

        // Clean up
        document.body.removeChild(textarea);
      }

      showSuccess(button) {
        // Update button state
        button.classList.add("copied");

        // Toggle icons - only show icon swap
        const copyIcon = button.querySelector(
          ".scp-social-sharing__icon--copy"
        );
        const checkIcon = button.querySelector(
          ".scp-social-sharing__icon--check"
        );

        if (copyIcon) copyIcon.style.display = "none";
        if (checkIcon) checkIcon.style.display = "block";

        // Update label if present
        const label = button.querySelector(".scp-social-sharing__label");
        const originalText = label ? label.textContent : "";
        if (label) label.textContent = "Copied!";

        // Reset after 3 seconds
        setTimeout(() => {
          button.classList.remove("copied");
          if (copyIcon) copyIcon.style.display = "block";
          if (checkIcon) checkIcon.style.display = "none";
          if (label) label.textContent = originalText;
        }, 3000);
      }
    }
  );
}

// Initialize all social sharing components on page load
document.addEventListener("DOMContentLoaded", () => {
  const socialSharingElements = document.querySelectorAll(
    "[data-social-sharing]"
  );

  socialSharingElements.forEach((element) => {
    if (!element.tagName.includes("-")) {
      // Wrap in custom element if not already
      const wrapper = document.createElement("scp-social-sharing");
      element.parentNode.insertBefore(wrapper, element);
      wrapper.appendChild(element);
    }
  });

  // Fallback: Direct event listener for copy buttons if custom element fails
  const copyButtons = document.querySelectorAll("[data-copy-link]");
  copyButtons.forEach((button) => {
    // Check if button already has a listener (from custom element)
    if (!button.dataset.listenerAttached) {
      button.dataset.listenerAttached = "true";
      button.addEventListener("click", async (e) => {
        e.preventDefault();
        const url = button.getAttribute("data-copy-link");

        try {
          if (navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(url);
            showCopySuccess(button);
          } else {
            fallbackCopy(url, button);
          }
        } catch (err) {
          console.error("Failed to copy:", err);
          fallbackCopy(url, button);
        }
      });
    }
  });
});

// Helper function for fallback copy
function fallbackCopy(text, button) {
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.style.position = "fixed";
  textarea.style.left = "-999999px";
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();

  try {
    document.execCommand("copy");
    showCopySuccess(button);
  } catch (err) {
    console.error("Copy failed:", err);
  }

  document.body.removeChild(textarea);
}

// Helper function to show copy success - only icon swap
function showCopySuccess(button) {
  button.classList.add("copied");

  const copyIcon = button.querySelector(".scp-social-sharing__icon--copy");
  const checkIcon = button.querySelector(".scp-social-sharing__icon--check");
  const label = button.querySelector(".scp-social-sharing__label");

  const originalText = label ? label.textContent : "";

  if (copyIcon) copyIcon.style.display = "none";
  if (checkIcon) checkIcon.style.display = "block";
  if (label) label.textContent = "Copied!";

  setTimeout(() => {
    button.classList.remove("copied");
    if (copyIcon) copyIcon.style.display = "block";
    if (checkIcon) checkIcon.style.display = "none";
    if (label) label.textContent = originalText;
  }, 3000);
}

// Analytics tracking (optional)
// Track when users click on social sharing buttons
document.addEventListener("click", (event) => {
  const button = event.target.closest(".scp-social-sharing__button");

  if (button && !button.hasAttribute("data-copy-link")) {
    const platform = button.className.match(
      /scp-social-sharing__button--(\w+)/
    )?.[1];

    if (platform && typeof window.dataLayer !== "undefined") {
      // Google Analytics 4 / GTM tracking
      window.dataLayer.push({
        event: "social_share",
        social_platform: platform,
        share_type: "product",
      });
    }
  }
});
